import { useMachine } from '@xstate/react'
import { GlobalShell } from 'components/GlobalShell'
import { useHistory, useParams } from 'lib/router'
import onboardingStateMachine, {
  OnboardingEvent,
  OnboardingStateMachineV1Enum,
} from 'hooks/OnboardingStateMachine/OnboardingStateMachineV1'
import { WorkEmail } from 'views/Onboarding/WorkEmail'
import { EmailVerification } from 'views/Onboarding/EmailVerification'
import CompanyName from 'views/Onboarding/CompanyName'
import { AddressView } from 'views/Onboarding/AddressView'
import CompanyExists from 'views/Onboarding/CompanyExists'
import {
  useCreateEmailToken,
  useGetEmailToken,
  useVerifyEmail,
} from 'api/endpoints/emails'
import {
  EmailMasked,
  EmailToken,
  OnboardingRecord,
  OrganizationOnboarding,
  OrganizationOnboardingDetails,
} from 'lib/generated/api'
import {
  useGetOrganizationOnboarding,
  useUpdateOnboardingRecordId,
} from 'api/endpoints/organizations'
import { AxiosResponse } from 'axios'
import { UseQueryResult, UseMutationResult } from 'react-query'
import { useEffect, useCallback } from 'react'
import { useContent } from 'lib/content/react'
import { useMessage } from 'contexts/MessageContext'
import { Event } from 'xstate'

const OnboardingFormContainer = () => {
  let history = useHistory()
  const { onboardingRecordId } = useParams<{
    onboardingRecordId: string
  }>()

  const { formatMessage } = useContent()
  const { addMessage } = useMessage()
  const showToast = useCallback(
    (id: string) => {
      addMessage({
        status: 'error',
        description: formatMessage({ id }),
        isClosable: false,
      })
    },
    [addMessage, formatMessage]
  )

  const [current, send] = useMachine(onboardingStateMachine, {
    actions: {
      goToPublish: () => {
        history.push(`/welcome/${onboardingRecordId}/publish`)
      },
    },
  })

  const {
    emailAddress,
    exchangeToken,
    emailToken,
    organizationName,
    formattedAddress,
    firstWorkspaceLocation,
  } = current.context
  const verifyEmail = useVerifyEmail()
  const updateOnboardingRecordId = useUpdateOnboardingRecordId() as UseMutationResult<
    OnboardingRecord,
    AxiosResponse
  >
  const createEmailToken = useCreateEmailToken() as UseMutationResult<
    EmailToken,
    AxiosResponse
  >
  const organizationOnboardingData = useGetOrganizationOnboarding({
    onboardingRecordId,
  }) as UseQueryResult<OrganizationOnboardingDetails, any>

  const ownerEmailToken = useGetEmailToken({
    emailToken: organizationOnboardingData?.data?.ownerEmailToken
      ? organizationOnboardingData?.data?.ownerEmailToken
      : '',
  }) as UseQueryResult<EmailMasked>

  const resendCode = (email: string, moveToNext: Boolean = true) => {
    verifyEmail.mutate(
      {
        emailAddress: {
          emailAddress: email,
        },
      },
      {
        onSuccess: ({ emailToken, exchangeToken }) => {
          let stateUpdate: Event<OnboardingEvent> = 'UPDATE_CONTEXT'
          if (emailToken) {
            stateUpdate = 'VERIFIED'
          } else if (moveToNext && exchangeToken) {
            stateUpdate = 'NEXT'
          }

          send(stateUpdate, {
            emailToken,
            email,
            exchangeToken,
          })
        },
      }
    )
  }

  const updateRecord = (data: OrganizationOnboarding) => {
    updateOnboardingRecordId.mutate(
      {
        onboardingRecordId,
        organizationOnboarding: {
          ...data,
        },
      },
      {
        onSuccess: () => {
          send('NEXT', { ...data })
        },
        onError: error => {
          switch (error.status) {
            case 409:
              send('COMPANY_EXISTS')
              break
            default:
              showToast('pmportal.error.unknown')
              break
          }
        },
      }
    )
  }

  useEffect(() => {
    if (organizationOnboardingData?.isSuccess) {
      send('UPDATE_CONTEXT', {
        ...current.context,
        ...organizationOnboardingData.data,
      })
    }
  }, [organizationOnboardingData.isSuccess])

  useEffect(() => {
    if (ownerEmailToken.isSuccess) {
      send('UPDATE_CONTEXT', {
        ...current.context,
        ...ownerEmailToken.data,
      })
    }
  }, [ownerEmailToken.isSuccess])

  return (
    <GlobalShell>
      {current.matches(OnboardingStateMachineV1Enum.WorkEmail) ? (
        <WorkEmail
          email={emailAddress}
          onNext={email => {
            if (email !== emailAddress) {
              resendCode(email, true)
            } else {
              send('VERIFIED')
            }
          }}
        />
      ) : current.matches(
          OnboardingStateMachineV1Enum.WorkEmailVerification
        ) ? (
        <EmailVerification
          email={emailAddress}
          onComplete={code => {
            createEmailToken.mutate(
              {
                emailTokenize: {
                  exchangeToken,
                  emailAddress,
                  verificationCode: code,
                },
              },
              {
                onSuccess: ({ emailToken }) => {
                  updateRecord({ ownerEmailToken: emailToken })
                },
                onError: error => {
                  switch (error.status) {
                    case 401: // code challenge/token mismatch
                    case 403: // code already rejected, mismatch, etc.
                    case 404: // no code found
                      showToast('pmportal.onboarding.emailTokenizeError')
                      break
                    case 409:
                      send('COMPANY_EXISTS')
                      break
                    default:
                      showToast('pmportal.error.unknown')
                      break
                  }
                },
              }
            )
          }}
          onResend={() => resendCode(emailAddress, false)}
        />
      ) : current.matches(OnboardingStateMachineV1Enum.OrganizationName) ? (
        <CompanyName
          companyName={organizationName}
          onNext={companyName => {
            if (companyName !== organizationName) {
              updateRecord({
                organizationName: companyName,
                ownerEmailToken: emailToken || undefined,
              })
            } else {
              send('NEXT')
            }
          }}
        />
      ) : current.matches(OnboardingStateMachineV1Enum.Address) ? (
        <AddressView
          address={formattedAddress}
          onNext={placeResult => {
            if (
              placeResult &&
              placeResult.place_id !== firstWorkspaceLocation
            ) {
              updateRecord({
                firstWorkspaceLocation: placeResult?.place_id,
              })
            } else if (formattedAddress) {
              send('NEXT')
            }
          }}
        />
      ) : current.matches(OnboardingStateMachineV1Enum.CompanyExists) ? (
        <CompanyExists
          companyName={organizationName}
          onExit={() =>
            (window.location.href = 'https://ritual.co/products/teams')
          }
        />
      ) : null}
    </GlobalShell>
  )
}

export default OnboardingFormContainer
